import React, { useState, useRef, useEffect } from 'react';
import styles from './Popup.module.css';
import clsx from 'clsx';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

export interface PopupProps {
    children: React.ReactElement;
    content: React.ReactNode;
    placement?: 'top' | 'bottom' | 'left' | 'right';
    className?: string;
    onClick?: () => void;
}

export const Popup: React.FC<PopupProps> = ({ children, content, placement = 'top', className, onClick }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const childRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    const calculatePosition = () => {
        if (!childRef.current || !popupRef.current) return;

        const childRect = childRef.current.getBoundingClientRect();
        const popupRect = popupRef.current.getBoundingClientRect();
        const spacing = 8;

        let top = 0;
        let left = 0;

        switch (placement) {
            case 'top':
                top = childRect.top - popupRect.height - spacing;
                left = childRect.left + (childRect.width - popupRect.width) / 2;
                break;
            case 'bottom':
                top = childRect.bottom + spacing;
                left = childRect.left + (childRect.width - popupRect.width) / 2;
                break;
            case 'left':
                top = childRect.top + (childRect.height - popupRect.height) / 2;
                left = childRect.left - popupRect.width - spacing;
                break;
            case 'right':
                top = childRect.top + (childRect.height - popupRect.height) / 2;
                left = childRect.right + spacing;
                break;
        }

        top += window.scrollY;
        left += window.scrollX;

        setPosition({ top, left });
    };

    const handleClick = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsVisible(prev => {
            const next = !prev;
            if (next) requestAnimationFrame(calculatePosition);
            return next;
        });
        onClick?.();
    };

    // cerrar al hacer click fuera
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (isVisible && childRef.current && !childRef.current.contains(event.target as Node)) {
                setIsVisible(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isVisible]);

    return (
        <>
            <div
                ref={childRef}
                onClick={handleClick}
                className={styles.popupTrigger}
            >
                {children}
            </div>

            <div
                ref={popupRef}
                className={clsx(
                    styles.popup,
                    styles[`popup${placement.charAt(0).toUpperCase() + placement.slice(1)}`],
                    className
                )}
                style={{
                    top: position.top,
                    left: position.left
                }}
                data-show={isVisible}
            >
                {content}
            </div>
        </>
    );
};
